const addressRegex = /^(?:(.+)!)?(?:([A-Z]+)(\d+)(?::([A-Z]+)(\d+))?|(\d+)(?::(\d+))?)$/;
const cellAddressRegex = /^([A-Z]+)(\d+)$/;
const alphabet = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ';
const EXCEL_MAX_COLUMN_INDEX = 16383; // Maximum column index in Excel (XFD)

export const lettersToIndex = (letters: string) => {
  if (!letters) return 0;
  return Array.from(letters).reduce((result, letter) => {
    return (result + 1) * 26 + alphabet.indexOf(letter);
  }, -1);
};

export const indexToLetters = (index: number) => {
  let result = [];
  while (index > 25) {
    const alphabetCounts = Math.trunc(index / 26);
    index = index - 26 * alphabetCounts;
    result.push(alphabet[alphabetCounts - 1]);
  }
  result.push(alphabet[index]);
  return result.join('');
};

export const cellAddressToIndexes = (address: string) => {
  const parsingResult = cellAddressRegex.exec(address);
  if (parsingResult) {
    const colIndex = lettersToIndex(parsingResult[1]);
    return [colIndex, +parsingResult[2] - 1];
  }
};

export const parseAddress = (address: string) => {
  const result = addressRegex.exec(address);

  if (!result) return null;

  const sheetName = result[1] || '';

  if (result[2]) {
    //single cell address (e.g. A1)
    const hasRangeNotation = result[4] !== undefined;
    return {
      sheetName: sheetName,
      startCol: result[2],
      startRow: result[3],
      endCol: hasRangeNotation ? result[4] : result[2], // A1 -> A1:A1
      endRow: hasRangeNotation ? result[5] : result[3],
      isEntireRow: false,
    };
  }

  //entire row address (e.g 2:2)
  if (result[6]) {
    return {
      sheetName: sheetName,
      startCol: '',
      startRow: result[6],
      endCol: '',
      endRow: result[7] || result[6],
      isEntireRow: true,
    };
  }

  return null;
};

const replaceQuotes = (text: string) => {
  return text.startsWith("'") && text.endsWith("'") ? text.substring(1, text.length - 1) : text;
};

export class RangeAddress {
  sheetName = '';
  startCol = 0;
  startRow = 0;
  endCol = 0;
  endRow = 0;
  isValid = true;

  static fromString(address: string) {
    const rangeAddress = new RangeAddress();

    const parsingResult = parseAddress(address);
    if (!parsingResult) {
      rangeAddress.isValid = false;
    } else {
      if (parsingResult.sheetName) {
        rangeAddress.sheetName = replaceQuotes(parsingResult.sheetName);
      }

      if (parsingResult.isEntireRow) {
        rangeAddress.startCol = 0;
        rangeAddress.endCol = EXCEL_MAX_COLUMN_INDEX;
        rangeAddress.startRow = +parsingResult.startRow - 1;
        rangeAddress.endRow = +parsingResult.endRow - 1;
      } else {
        rangeAddress.startCol = lettersToIndex(parsingResult.startCol);
        rangeAddress.startRow = +parsingResult.startRow - 1;
        rangeAddress.endCol = lettersToIndex(parsingResult.endCol);
        rangeAddress.endRow = +parsingResult.endRow - 1;
      }
    }

    return rangeAddress;
  }

  static fromCoordinates(startCol: number, startRow: number, endCol: number, endRow: number, from?: RangeAddress) {
    const rangeAddress = new RangeAddress();
    rangeAddress.startCol = startCol;
    rangeAddress.endCol = endCol;
    rangeAddress.startRow = startRow;
    rangeAddress.endRow = endRow;

    rangeAddress.sheetName = from?.sheetName || '';

    return rangeAddress;
  }

  get address() {
    if (this.isEntireRow) {
      const startRowLetter = this.startRow + 1;
      const endRowLetter = this.endRow + 1;

      return this.startRow === this.endRow
        ? `${startRowLetter}:${startRowLetter}`
        : `${startRowLetter}:${endRowLetter}`;
    }

    const startColumnLetter = indexToLetters(this.startCol);
    const startRowLetter = this.startRow + 1;
    const endColumnLetter = indexToLetters(this.endCol);
    const endRowLetter = this.endRow + 1;

    return `${startColumnLetter}${startRowLetter}:${endColumnLetter}${endRowLetter}`;
  }

  get addressWithSheet() {
    if (!this.sheetName) {
      return this.address;
    }

    return `${this.sheetName}!${this.address}`;
  }

  get topLeftCellAddress() {
    const startColumnLetter = indexToLetters(this.startCol);
    const startRowIndex = this.startRow + 1;
    return `${startColumnLetter}${startRowIndex}`;
  }

  getColumnsAfter(columnCount: number) {
    return RangeAddress.fromCoordinates(this.endCol + 1, this.startRow, this.endCol + columnCount, this.endRow, this);
  }

  getRowsAfter(rowCount: number) {
    return RangeAddress.fromCoordinates(this.startCol, this.endRow + 1, this.endCol, this.endRow + rowCount, this);
  }

  getRowsAbove(rowCount: number) {
    return RangeAddress.fromCoordinates(
      this.startCol,
      this.startRow - rowCount < 0 ? 0 : this.startRow - rowCount,
      this.endCol,
      this.startRow - 1 < 0 ? 0 : this.startRow - 1,
      this,
    );
  }

  splitByColumns(columnIndexes: number[]) {
    const sortedIndexes = [...columnIndexes].sort((a, b) => a - b);
    const result: RangeAddress[] = [];
    let subRangeAddress: RangeAddress | null = null;

    for (const columnIndex of sortedIndexes) {
      if (!subRangeAddress || columnIndex - subRangeAddress.endCol > 1) {
        subRangeAddress = RangeAddress.fromCoordinates(columnIndex, this.startRow, columnIndex, this.endRow, this);
        result.push(subRangeAddress);
        continue;
      }

      subRangeAddress.endCol++;
    }

    return result;
  }

  getIntersection(range: RangeAddress) {
    if (!range.isValid || !this.isValid) {
      throw Error("Can't process invalid range");
    }

    if (!!this.sheetName && !!range.sheetName && this.sheetName !== range.sheetName) {
      return null;
    }

    const startCol = Math.max(range.startCol, this.startCol);
    const startRow = Math.max(range.startRow, this.startRow);
    const endCol = Math.min(range.endCol, this.endCol);
    const endRow = Math.min(range.endRow, this.endRow);

    if (startCol > endCol || startRow > endRow) {
      return null;
    }

    return RangeAddress.fromCoordinates(startCol, startRow, endCol, endRow, this);
  }

  getRow(index: number) {
    return RangeAddress.fromCoordinates(this.startCol, this.startRow + index, this.endCol, this.startRow + index, this);
  }

  getColumn(index: number) {
    return RangeAddress.fromCoordinates(this.startCol + index, this.startRow, this.startCol + index, this.endRow, this);
  }

  getCell(rowIndex: number, colIndex: number) {
    const row = this.startRow + rowIndex;
    const col = this.startCol + colIndex;

    if (row < this.startRow || row > this.endRow || col < this.startCol || col > this.endCol) {
      throw new Error(`Cell at (${rowIndex}, ${colIndex}) is outside the range bounds`);
    }

    const cellAddress = RangeAddress.fromCoordinates(col, row, col, row, this);
    cellAddress.sheetName = this.sheetName;
    return cellAddress;
  }

  addColumns(columnCount: number) {
    this.endCol += columnCount;
  }

  addRows(rowCount: number) {
    this.endRow += rowCount;
  }

  get isCellAddress() {
    return this.startCol === this.endCol && this.startRow === this.endRow;
  }

  get isEntireRow() {
    return this.startCol === 0 && this.endCol === EXCEL_MAX_COLUMN_INDEX;
  }

  getResizedRange(rowDelta: number, colDelta: number) {
    return RangeAddress.fromCoordinates(
      this.startCol,
      this.startRow,
      this.endCol + colDelta,
      this.endRow + rowDelta,
      this,
    );
  }

  getAbsoluteResizedRange(rowCount: number, colCount: number) {
    return RangeAddress.fromCoordinates(
      this.startCol,
      this.startRow,
      this.startCol + (colCount ? colCount - 1 : 0),
      this.startRow + (rowCount ? rowCount - 1 : 0),
      this,
    );
  }

  get rowCount() {
    return this.endRow - this.startRow + 1;
  }
}
